// src/handover/PickupHandoverForm.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { Button } from '../ui';
import { createAuditedClient } from '../utils/auditClient';
import { getUrl } from 'aws-amplify/storage';
import { fromZonedTime, toZonedTime } from 'date-fns-tz';
import { format } from 'date-fns';
import {
  cardStyle,
  labelStyle,
  inputStyle,
  textareaStyle,
  sectionTitleStyle,
  twoColGrid,
  threeColGrid,
  COLORS,
  helperTextStyle,
} from './handoverStyles';

import {
  fuelStringToPercent,
  uploadFiles,
  dataUrlToFile,
  uploadedItemsToKeys,
  countPendingUploads,
  countFailedUploads,
} from './handoverUtils';
import { getPickupContractText } from './handoverContracts';
import { SubmitHandoverForm } from './handoverQueries';

import SignaturePad from '../components/handover/SignaturePad';
import FileUploadWithPreview from '../components/handover/FileUploadWithPreview';

const client = createAuditedClient('PickupHandoverForm');

const UpdateReservationChecklist = /* GraphQL */ `
  mutation UpdateReservation($input: UpdateReservationInput!) {
    updateReservation(input: $input) {
      id
      pickupChecklistStatus
      dropoffChecklistStatus
      updatedAt
    }
  }
`;
const GetReservation = /* GraphQL */ `
  query GetReservation($id: ID!) {
    getReservation(id: $id) {
      id
      CompanyId
      paymentStatus
      amountPaid
      rentalAmount
      rentalCurrency
      depositCurrency
      extras

      updatedAt
    }
  }
`;

const CURRENCY_OPTIONS = ['USD', 'EUR', 'GEL', 'LEK', 'GBP', 'AED'];

function safeNum(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function fmtMoney(n) {
  const x = Number(n);
  if (!Number.isFinite(x)) return '';
  return x.toFixed(2);
}

function addMoneyBucket(buckets, currency, key, amount) {
  const c = (currency || '').trim().toUpperCase();
  if (!c) return buckets;

  const a = safeNum(amount);
  if (!a) return buckets;

  if (!buckets[c]) {
    buckets[c] = { rental: 0, extras: 0, insurance: 0, delivery: 0, grand: 0 };
  }
  buckets[c][key] += a;
  buckets[c].grand += a;
  return buckets;
}

const CROSS_BORDER_COUNTRY_OPTIONS = ['Kosovo', 'Greece', 'North Macedonia', 'Montenegro'];
const CROSS_BORDER_COUNTRY_TOKEN_PREFIX = 'cross_border_country:';

function normalizeCrossBorderCountryKey(value) {
  return String(value || '').trim().toLowerCase().replace(/[\s_-]+/g, '');
}

function normalizeCrossBorderCountries(value) {
  const byKey = CROSS_BORDER_COUNTRY_OPTIONS.reduce((acc, country) => {
    acc[normalizeCrossBorderCountryKey(country)] = country;
    return acc;
  }, {});
  const raw = [];
  const add = (item) => {
    if (Array.isArray(item)) {
      item.forEach(add);
      return;
    }
    const s = String(item || '').trim();
    if (!s) return;
    if (s.includes(',')) {
      s.split(',').forEach(add);
      return;
    }
    raw.push(s);
  };

  add(value);

  const out = [];
  for (const item of raw) {
    const clean = item.replace(/^cross[\s_-]*border[\s_-]*country\s*[:=]\s*/i, '');
    const country = byKey[normalizeCrossBorderCountryKey(clean)];
    if (country && !out.includes(country)) out.push(country);
  }
  return out;
}

function parseAwsJson(value) {
  if (!value) return null;
  if (typeof value !== 'string') return value;
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
}

function crossBorderCountriesFromExtras(extrasValue) {
  const parsed = parseAwsJson(extrasValue);
  if (!parsed) return [];
  if (Array.isArray(parsed)) {
    const out = [];
    for (const item of parsed) {
      const token = String(item || '').trim();
      if (!token.toLowerCase().startsWith(CROSS_BORDER_COUNTRY_TOKEN_PREFIX)) continue;
      for (const country of normalizeCrossBorderCountries(token)) {
        if (!out.includes(country)) out.push(country);
      }
    }
    return out;
  }
  if (typeof parsed === 'object') {
    return normalizeCrossBorderCountries(
      parsed.crossBorderCountries ||
        parsed.crossBorderDestinationCountries ||
        parsed.crossBorderCountry
    );
  }
  return [];
}

function MoneyInput({
  label,
  amount,
  currency,
  onAmount,
  onCurrency,
  disabled,
  maxWidth,
  isMobile,
}) {
  return (
    <label>
      <span style={labelStyle}>{label}</span>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <input
          type="number"
          step="0.01"
          style={{
            ...inputStyle,
            maxWidth: isMobile ? '100%' : maxWidth || 160,
            flex: isMobile ? '1 1 100%' : '0 0 auto',
          }}
          value={amount}
          onChange={(e) => onAmount(e.target.value)}
          disabled={disabled}
        />
        <select
          style={{
            ...inputStyle,
            maxWidth: isMobile ? '100%' : 140,
            flex: isMobile ? '1 1 100%' : '0 0 auto',
          }}
          value={currency}
          onChange={(e) => onCurrency(e.target.value)}
          disabled={disabled}
        >
          <option value="">Currency</option>
          {CURRENCY_OPTIONS.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
      </div>
    </label>
  );
}

// Helper: deposit suggestion by country + category
function getSuggestedDeposit(location, category) {
  if (!location) return null;

  if (location === 'GE') {
    // Georgia: flat 400 USD
    return 400;
  }

  if (location === 'AL') {
    // Albania per category
    switch (category) {
      case 'ECONOMY':
        return 600;
      case 'COMPACT':
      case 'COMPACT_ELITE':
        return 800;
      case 'INTERMEDIATE':
      case 'INTERMEDIATE_ELITE':
      case 'INTERMEDIATE_SUV':
      case 'MINIVAN':
        return 1000;
      case 'COMPACT_SUV_CFAR':
        return 1000; // "COMPACT SUV"
      case 'SUV_ELITE':
        return 1200; // "SUV"
      default:
        return null;
    }
  }

  return null;
}
// Company tz from location prop
function companyTzForLocation(location) {
  if (String(location).toUpperCase() === 'AL') return 'Europe/Tirane';
  if (String(location).toUpperCase() === 'GE') return 'Asia/Tbilisi';
  return 'UTC';
}

function splitCompanyDateTime(utcIso, tz) {
  if (!utcIso) return { date: '', time: '' };
  const d = new Date(utcIso);
  if (Number.isNaN(d.getTime())) return { date: '', time: '' };
  const z = toZonedTime(d, tz);
  return {
    date: format(z, 'yyyy-MM-dd'),
    time: format(z, 'HH:mm'),
  };
}

function utcDateFromCompanyParts(dateYmd, timeHm, tz) {
  if (!dateYmd || !timeHm) return null;
  const s = `${dateYmd}T${timeHm}:00`;
  const d = fromZonedTime(s, tz); // interpret as wall-clock in tz -> UTC date
  return Number.isNaN(d.getTime()) ? null : d;
}
// 💸 Build a human-readable payment summary from reservation (legacy note)
// 💸 UI-only payment label (NOT stored in notes / NOT emailed)
function buildPaymentLabel(res) {
  if (!res) return null;

  const rawStatus = res.paymentStatus || 'NOT_PAID';
  const status = String(rawStatus).toUpperCase();

  const rental =
    res.rentalAmount != null && !Number.isNaN(Number(res.rentalAmount))
      ? Number(res.rentalAmount)
      : null;

  const paid =
    res.amountPaid != null && !Number.isNaN(Number(res.amountPaid))
      ? Number(res.amountPaid)
      : null;

  const currency = res.rentalCurrency || res.depositCurrency || '';

  const fmt = (n) =>
    n == null || Number.isNaN(n)
      ? null
      : `${n.toFixed(2)}${currency ? ` ${currency}` : ''}`;

  // If nothing meaningful is known, don't show anything
  if (!rental && !paid && (!res.paymentStatus || status === 'NOT_PAID')) return null;

  if (status === 'PREPAID') {
    const amount = rental != null ? rental : paid;
    return {
      tone: 'good',
      text: amount != null ? `PREPAID — ${fmt(amount)} received.` : 'PREPAID in full.',
    };
  }

  if (status === 'PARTIALLY_PAID') {
    if (rental != null && paid != null) {
      const remaining = Math.max(0, rental - paid);
      return {
        tone: 'warn',
        text: `PARTIALLY PAID — ${fmt(paid)} received, ${fmt(remaining)} remaining.`,
      };
    }
    if (paid != null) {
      return { tone: 'warn', text: `PARTIALLY PAID — ${fmt(paid)} received.` };
    }
    return { tone: 'warn', text: 'PARTIALLY PAID (amount not recorded).' };
  }

  // NOT_PAID
  if (rental != null) {
    return { tone: 'bad', text: `NOT PAID — ${fmt(rental)} due at pickup.` };
  }
  return { tone: 'bad', text: 'NOT PAID (amount not recorded).' };
}


export default function PickupHandoverForm({
  reservationId,
  reservation,
  cars,
  claims,
  location, // 'GE' | 'AL'
  readOnly = false,
  existingHandover = null,
  pushAlert,
}) {
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const [isMobile, setIsMobile] = useState(false);
  const isAlbania = location === 'AL';
  const defaultCurrency = location === 'AL' ? 'EUR' : 'USD';
  const companyTz = useMemo(() => companyTzForLocation(location), [location]);

  const [idUploads, setIdUploads] = useState([]);
  const [odoFuelUploads, setOdoFuelUploads] = useState([]);
  const [carPhotoUploads, setCarPhotoUploads] = useState([]);

  // ✅ read-only URLs
  const [existingIdPhotoUrls, setExistingIdPhotoUrls] = useState([]);
  const [existingOdoFuelUrls, setExistingOdoFuelUrls] = useState([]);
  const [existingCarPhotoUrls, setExistingCarPhotoUrls] = useState([]);
  const [freshReservation, setFreshReservation] = useState(reservation || null);

  useEffect(() => {
    setFreshReservation(reservation || null);
  }, [reservation]);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (!reservationId) return;
      try {
        const res = await client.graphql({
          query: GetReservation,
          variables: { id: reservationId },
          authMode: 'userPool',
        });
        const r = res?.data?.getReservation || null;
        if (!cancelled && r) setFreshReservation(r);
      } catch (e) {
        console.warn('Failed to refetch reservation', e);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [reservationId]);

  const [form, setForm] = useState({
    agreementDate: new Date().toISOString().substring(0, 10),
    agreementLocation: '',

    lesseeFirstName: '',
    lesseeLastName: '',
    lesseeEmail: '',
    lesseeEmailConfirm: '',
    lesseePhone: '',
    idNumber: '',
    drivingLicenseNumber: '',
    drivingLicenseCountry: '',

    carId: '',
    includeOriginallyReservedCar: false,
    carDeliveredWashed: 'washed',
    currentFuel: '',
    odometer: '',

    pickupDate: '',
    pickupTime: '',
    pickupLocation: '',
    dropoffDate: '',
    dropoffTime: '',
    dropoffLocation: '',

    rentalDaysManual: '',
    // ===== pricing: amount + currency per field =====
    dailyRateAmount: '',
    dailyRateCurrency: defaultCurrency,
    insurancePerDayAmount: '',
    insurancePerDayCurrency: defaultCurrency,
    deliveryChargeAmount: '',
    deliveryChargeCurrency: defaultCurrency,
    extrasCurrency: defaultCurrency,

    // extras (booleans)
    extrasCarSeat: false,
    extrasBooster: false,
    extrasSnowChains: false,
    extrasExtraDriver1: false,
    extrasExtraDriver2: false,

    // deposit (amount + currency)
    depositAmount: '',
    depositCurrency: defaultCurrency,

    extraNotes: '',
    lesseeSignatureDataUrl: '',
  });

  // Multi-line payments (method + amount + currency)
  const [payments, setPayments] = useState([
    { method: '', amount: '', currency: defaultCurrency, note: '' },
  ]);

  function addPaymentRow() {
    setPayments((prev) => [
      ...prev,
      { method: 'CASH', amount: '', currency: defaultCurrency, note: '' },
    ]);
  }
  function removePaymentRow(idx) {
    setPayments((prev) => prev.filter((_, i) => i !== idx));
  }
  function updatePayment(idx, patch) {
    setPayments((prev) => prev.map((p, i) => (i === idx ? { ...p, ...patch } : p)));
  }

  const [submitStage, setSubmitStage] = useState(null);
  const [submitPct, setSubmitPct] = useState(0);
  const [submitDetail, setSubmitDetail] = useState('');
  const [submitStartedAt, setSubmitStartedAt] = useState(null);

  function setStage(stage, pct, detail = '') {
    setSubmitStage(stage);
    setSubmitPct(pct);
    setSubmitDetail(detail);
  }

  const pendingPhotoUploads =
    countPendingUploads(idUploads) +
    countPendingUploads(odoFuelUploads) +
    countPendingUploads(carPhotoUploads);
  const failedPhotoUploads =
    countFailedUploads(idUploads) +
    countFailedUploads(odoFuelUploads) +
    countFailedUploads(carPhotoUploads);

  const [initializedFromReservation, setInitializedFromReservation] = useState(false);
  const [initializedFromExisting, setInitializedFromExisting] = useState(false);

  const [lesseeEmailTouched, setLesseeEmailTouched] = useState(false);
  const [lesseeEmailConfirmTouched, setLesseeEmailConfirmTouched] = useState(false);

  // In-form alert banner (fallback if pushAlert is not provided)
  const [banner, setBanner] = useState(null);
  function showAlert(type, title, message) {
    const payload = { type, title: title || '', message: message || '' };

    if (typeof pushAlert === 'function') pushAlert(payload);
    else setBanner(payload);
  }

  // ===== responsive layout =====
  useEffect(() => {
    function handleResize() {
      if (typeof window === 'undefined') return;
      setIsMobile(window.innerWidth <= 640);
    }
    handleResize();
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, []);

  const stackedGrid = { display: 'grid', gridTemplateColumns: '1fr', gap: 14 };
  const twoColResponsive = isMobile ? stackedGrid : twoColGrid;
  const threeColResponsive = isMobile ? stackedGrid : threeColGrid;

  // Contract collapse state
  const [showContract, setShowContract] = useState(false);
  const contractText = useMemo(() => getPickupContractText(location), [location]);

  // Prefill from existing handover (view mode)
  useEffect(() => {
    if (!existingHandover || initializedFromExisting) return;

    let parsed = {};
    try {
      if (existingHandover.equipmentNotes) {
        parsed = JSON.parse(existingHandover.equipmentNotes) || {};
      }
    } catch (e) {
      console.warn('Failed to parse equipmentNotes from existing handover', e);
    }

    // Backward compatibility: older handovers used dailyRate/insurancePerDay/deliveryCharge + rentalCurrency
    const legacyRentalCurrency = parsed.rentalCurrency || '';
    const migrated = {
      dailyRateAmount: parsed.dailyRateAmount ?? parsed.dailyRate ?? '',
      dailyRateCurrency: parsed.dailyRateCurrency ?? legacyRentalCurrency ?? '',
      insurancePerDayAmount: parsed.insurancePerDayAmount ?? parsed.insurancePerDay ?? '',
      insurancePerDayCurrency: parsed.insurancePerDayCurrency ?? legacyRentalCurrency ?? '',
      deliveryChargeAmount: parsed.deliveryChargeAmount ?? parsed.deliveryCharge ?? '',
      deliveryChargeCurrency: parsed.deliveryChargeCurrency ?? legacyRentalCurrency ?? '',
      extrasCurrency: parsed.extrasCurrency ?? legacyRentalCurrency ?? '',
    };

    setForm((prev) => ({
      ...prev,
      ...parsed,
      ...migrated,
      // booleans
      extrasCarSeat: !!(parsed.extrasCarSeat ?? prev.extrasCarSeat),
      extrasBooster: !!(parsed.extrasBooster ?? prev.extrasBooster),
      extrasSnowChains: !!(parsed.extrasSnowChains ?? prev.extrasSnowChains),
      extrasExtraDriver1: !!(parsed.extrasExtraDriver1 ?? prev.extrasExtraDriver1),
      extrasExtraDriver2: !!(parsed.extrasExtraDriver2 ?? prev.extrasExtraDriver2),
      // keep agreement date if not present
      agreementDate: parsed.agreementDate || prev.agreementDate,
    }));

    if (Array.isArray(parsed.payments)) {
      const normalized = parsed.payments
        .map((p) => ({
          method: String(p?.method || 'CASH').toUpperCase(),
          amount: p?.amount != null ? String(p.amount) : '',
          currency: String(p?.currency || ''),
          note: String(p?.note || ''),
        }))
        .filter((p) => p.method || p.amount || p.currency || p.note);
      if (normalized.length > 0) setPayments(normalized);
    }

    setSubmitted(true);
    setInitializedFromExisting(true);
  }, [existingHandover, initializedFromExisting]);

  // ✅ load photo URLs from existingHandover.carPhotoKeys
  useEffect(() => {
    if (!existingHandover || !Array.isArray(existingHandover.carPhotoKeys)) {
      setExistingIdPhotoUrls([]);
      setExistingOdoFuelUrls([]);
      setExistingCarPhotoUrls([]);
      return;
    }

    const allKeys = existingHandover.carPhotoKeys || [];

    const idKeys = allKeys.filter((k) => typeof k === 'string' && k.includes('handover/pickup/id'));
    const odoKeys = allKeys.filter((k) => typeof k === 'string' && k.includes('handover/pickup/odo'));
    const carKeys = allKeys.filter((k) => typeof k === 'string' && k.includes('handover/pickup/car'));

    const loadUrls = async (keys) => {
      const results = await Promise.all(
        keys.map(async (key) => {
          try {
            const { url } = await getUrl({ key });
            return { key, url: url.toString() };
          } catch (err) {
            console.warn('Failed to get URL for key', key, err);
            return null;
          }
        })
      );
      return results.filter(Boolean);
    };

    (async () => {
      const [idUrls, odoUrls, carUrls] = await Promise.all([
        loadUrls(idKeys),
        loadUrls(odoKeys),
        loadUrls(carKeys),
      ]);
      setExistingIdPhotoUrls(idUrls);
      setExistingOdoFuelUrls(odoUrls);
      setExistingCarPhotoUrls(carUrls);
    })();
  }, [existingHandover]);
  useEffect(() => {
    const def = location === 'AL' ? 'EUR' : 'USD';

    setForm((prev) => {
      const patch = {};
      const fields = [
        'dailyRateCurrency',
        'insurancePerDayCurrency',
        'deliveryChargeCurrency',
        'extrasCurrency',
        'depositCurrency',
      ];

      for (const f of fields) {
        if (!String(prev[f] || '').trim()) patch[f] = def;
      }

      return Object.keys(patch).length ? { ...prev, ...patch } : prev;
    });

    setPayments((prev) =>
      (prev || []).map((p) =>
        String(p?.currency || '').trim() ? p : { ...p, currency: def }
      )
    );
  }, [location]);


  // Prefill from reservation (only if we didn't load from existing yet)
  useEffect(() => {
    if (!freshReservation || initializedFromReservation || initializedFromExisting) return;

    let firstName = '';
    let lastName = '';
    if (freshReservation.customerName) {
      const parts = freshReservation.customerName.trim().split(' ');
      firstName = parts[0] || '';
      lastName = parts.slice(1).join(' ');
    }

    const phone =
      Array.isArray(freshReservation.phoneNumbers) && freshReservation.phoneNumbers.length > 0
        ? freshReservation.phoneNumbers[0]
        : '';

    const pickupIso = freshReservation.pickupAt || freshReservation.start || null;
    const dropoffIso = freshReservation.dropoffAt || freshReservation.end || null;

    const s = pickupIso ? splitCompanyDateTime(pickupIso, companyTz) : { date: '', time: '' };
    const e = dropoffIso ? splitCompanyDateTime(dropoffIso, companyTz) : { date: '', time: '' };

    const pickupDate = s.date;
    const pickupTime = s.time;
    const dropoffDate = e.date;
    const dropoffTime = e.time;

    // infer daily rate using FULL datetime diff, not just dates
    let inferredDailyRate = '';
    if (freshReservation.rentalAmount != null && pickupIso && dropoffIso) {
      const start = pickupIso ? new Date(pickupIso) : null;   // pickupIso is UTC -> OK
      const end = dropoffIso ? new Date(dropoffIso) : null;   // dropoffIso is UTC -> OK
      if (start && end) {
        const ms = end.getTime() - start.getTime();
        const dayMs = 1000 * 60 * 60 * 24;
        const days = ms > 0 ? Math.ceil(ms / dayMs) : 0;

        if (days > 0) {
          const total = Number(freshReservation.rentalAmount) || 0;
          inferredDailyRate = (total / days).toFixed(2);
        }
      }
    }
    setForm((prev) => {
      const defaultCurrency =
        freshReservation.rentalCurrency ||
        prev.dailyRateCurrency ||
        (isAlbania ? 'EUR' : 'USD');

      return {
        ...prev,
        lesseeFirstName: firstName || prev.lesseeFirstName,
        lesseeLastName: lastName || prev.lesseeLastName,
        lesseeEmail: prev.lesseeEmail || '',
        lesseeEmailConfirm: prev.lesseeEmailConfirm || '',
        lesseePhone: phone || prev.lesseePhone,

        pickupDate: pickupDate || prev.pickupDate,
        pickupTime: pickupTime || prev.pickupTime,
        dropoffDate: dropoffDate || prev.dropoffDate,
        dropoffTime: dropoffTime || prev.dropoffTime,

        pickupLocation:
          freshReservation.pickupLocation ||
          prev.pickupLocation ||
          (isAlbania ? 'Tirana International Airport (TIA)' : 'Tbilisi Airport'),

        dropoffLocation:
          freshReservation.dropoffLocation ||
          prev.dropoffLocation ||
          (isAlbania ? 'Tirana International Airport (TIA)' : 'Tbilisi Airport'),

        carId: freshReservation.carId || prev.carId,

        depositAmount:
          freshReservation.depositAmount != null ? String(freshReservation.depositAmount) : prev.depositAmount,

        depositCurrency:
          freshReservation.depositCurrency || prev.depositCurrency || (isAlbania ? 'EUR' : 'USD'),

        // pricing defaults
        dailyRateAmount: inferredDailyRate || prev.dailyRateAmount,
        dailyRateCurrency: prev.dailyRateCurrency || defaultCurrency,

        insurancePerDayAmount: prev.insurancePerDayAmount || '',
        insurancePerDayCurrency: prev.insurancePerDayCurrency || defaultCurrency,

        deliveryChargeAmount: prev.deliveryChargeAmount || '',
        deliveryChargeCurrency: prev.deliveryChargeCurrency || defaultCurrency,

        extrasCurrency: prev.extrasCurrency || defaultCurrency,

        // IMPORTANT: do NOT auto-write payment text into notes anymore
        extraNotes: prev.extraNotes || '',
      };
    });


    setInitializedFromReservation(true);
  }, [freshReservation, initializedFromReservation, initializedFromExisting, isAlbania]);
  useEffect(() => {
    console.log('handover reservation paymentStatus:', reservation?.paymentStatus, reservation?.amountPaid, reservation?.updatedAt);
  }, [reservation]);
  function updateField(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  function handleCheckbox(field) {
    setForm((prev) => ({ ...prev, [field]: !prev[field] }));
  }

  // selected car (for category, plate, etc.)
  const selectedCar = useMemo(() => {
    const id = form.carId || reservation?.carId;
    if (!id || !Array.isArray(cars)) return null;
    return cars.find((c) => c.id === id) || null;
  }, [cars, form.carId, reservation?.carId]);

  const originalReservedCar = useMemo(() => {
    const hasSignedSnapshot =
      Object.prototype.hasOwnProperty.call(form, 'originalReservedCarId') ||
      Object.prototype.hasOwnProperty.call(form, 'originalReservedCarName') ||
      Object.prototype.hasOwnProperty.call(form, 'originalReservedCarPlate');
    const id = String(
      (hasSignedSnapshot ? form.originalReservedCarId : reservation?.originalCarId) || ''
    ).trim();
    const fleetCar = (cars || []).find((c) => c.id === id) || null;
    const name = String(
      hasSignedSnapshot
        ? form.originalReservedCarName || ''
        : reservation?.originalCarName || fleetCar?.name || ''
    ).trim();
    const plate = String(
      hasSignedSnapshot
        ? form.originalReservedCarPlate || ''
        : reservation?.originalCarPlate || fleetCar?.plate || ''
    ).trim();

    if (!id && !name && !plate) return null;

    return {
      id,
      name,
      plate,
      label: plate ? `${name || 'Car'} (${plate})` : name || id,
    };
  }, [
    cars,
    form,
    reservation?.originalCarId,
    reservation?.originalCarName,
    reservation?.originalCarPlate,
  ]);

  const originalReservedCarDiffersFromPickupCar = useMemo(() => {
    if (!originalReservedCar) return false;

    const selectedId = String(form.carId || reservation?.carId || '').trim();
    if (originalReservedCar.id && selectedId) {
      return originalReservedCar.id !== selectedId;
    }

    if (!selectedCar) return true;

    const originalName = originalReservedCar.name.toLocaleLowerCase();
    const originalPlate = originalReservedCar.plate.toLocaleLowerCase();
    const selectedName = String(selectedCar.name || '').trim().toLocaleLowerCase();
    const selectedPlate = String(selectedCar.plate || '').trim().toLocaleLowerCase();
    const sameName = !!originalName && originalName === selectedName;
    const samePlate = !!originalPlate && originalPlate === selectedPlate;

    return !(samePlate || (sameName && (!originalPlate || originalPlate === selectedPlate)));
  }, [form.carId, originalReservedCar, reservation?.carId, selectedCar]);

  const reservedCrossBorderCountries = useMemo(() => {
    const fromFresh = crossBorderCountriesFromExtras(freshReservation?.extras);
    if (fromFresh.length) return fromFresh;
    return crossBorderCountriesFromExtras(reservation?.extras);
  }, [freshReservation?.extras, reservation?.extras]);

  // Rental days (include TIME, not just date)
  const rentalDays = useMemo(() => {
    const startUtc = utcDateFromCompanyParts(form.pickupDate, form.pickupTime || '00:00', companyTz);
    const endUtc = utcDateFromCompanyParts(form.dropoffDate, form.dropoffTime || '00:00', companyTz);

    if (!startUtc || !endUtc) return 0;

    const ms = endUtc.getTime() - startUtc.getTime();
    if (ms <= 0) return 0;

    return Math.ceil(ms / (1000 * 60 * 60 * 24));
  }, [form.pickupDate, form.pickupTime, form.dropoffDate, form.dropoffTime, companyTz]);


  // Extras pricing per rules
  const totalExtras = useMemo(() => {
    if (!rentalDays) return 0;
    let perDay = 0;

    if (isAlbania) {
      // Albania: 7 EUR/day for drivers + car/booster seats, snow chains = included
      if (form.extrasCarSeat) perDay += 7;
      if (form.extrasBooster) perDay += 7;
      if (form.extrasSnowChains) perDay += 0;
      if (form.extrasExtraDriver1) perDay += 7;
      if (form.extrasExtraDriver2) perDay += 7;
    } else {
      // Georgia + others: 5 per day for everything
      const per = 5;
      if (form.extrasCarSeat) perDay += per;
      if (form.extrasBooster) perDay += per;
      if (form.extrasSnowChains) perDay += per;
      if (form.extrasExtraDriver1) perDay += per;
      if (form.extrasExtraDriver2) perDay += per;
    }

    return rentalDays * perDay;
  }, [
    rentalDays,
    isAlbania,
    form.extrasCarSeat,
    form.extrasBooster,
    form.extrasSnowChains,
    form.extrasExtraDriver1,
    form.extrasExtraDriver2,
  ]);

  const totalRental = useMemo(() => {
    const d = safeNum(form.dailyRateAmount);
    if (!rentalDays || !d) return 0;
    return rentalDays * d;
  }, [rentalDays, form.dailyRateAmount]);

  const totalInsurance = useMemo(() => {
    const perDay = safeNum(form.insurancePerDayAmount);
    if (!rentalDays || !perDay) return 0;
    return rentalDays * perDay;
  }, [rentalDays, form.insurancePerDayAmount]);

  const deliveryCharge = useMemo(() => safeNum(form.deliveryChargeAmount), [form.deliveryChargeAmount]);

  // Totals separated by currency (NO conversion)
  const totalsByCurrency = useMemo(() => {
    const buckets = {};
    addMoneyBucket(buckets, form.dailyRateCurrency, 'rental', totalRental);
    addMoneyBucket(buckets, form.insurancePerDayCurrency, 'insurance', totalInsurance);
    addMoneyBucket(buckets, form.deliveryChargeCurrency, 'delivery', deliveryCharge);
    addMoneyBucket(buckets, form.extrasCurrency, 'extras', totalExtras);
    return buckets;
  }, [
    form.dailyRateCurrency,
    form.insurancePerDayCurrency,
    form.deliveryChargeCurrency,
    form.extrasCurrency,
    totalRental,
    totalInsurance,
    deliveryCharge,
    totalExtras,
  ]);

  // Email validation
  const emailMismatch =
    form.lesseeEmail &&
    form.lesseeEmailConfirm &&
    form.lesseeEmail !== form.lesseeEmailConfirm;

  const carOptions = useMemo(() => {
    if (!cars || cars.length === 0) return [];
    return cars
      .filter((c) => c.active)
      .map((c) =>
        c.plate ? { value: c.id, label: `${c.name} (${c.plate})` } : { value: c.id, label: c.name }
      );
  }, [cars]);

  // Suggested deposit & autofill when empty
  const suggestedDeposit = useMemo(() => {
    if (!selectedCar) return null;
    return getSuggestedDeposit(location, selectedCar.category);
  }, [selectedCar, location]);

  useEffect(() => {
    const beforeUnload = (e) => {
      if (!submitting) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', beforeUnload);
    return () => window.removeEventListener('beforeunload', beforeUnload);
  }, [submitting]);

  useEffect(() => {
    if (!selectedCar) return;
    if (!suggestedDeposit) return;

    setForm((prev) => {
      if (prev.depositAmount) return prev;

      const nextCurrency = prev.depositCurrency || (isAlbania ? 'EUR' : 'USD');
      return { ...prev, depositAmount: String(suggestedDeposit), depositCurrency: nextCurrency };
    });
  }, [selectedCar, suggestedDeposit, isAlbania]);
  // Auto-add "amount received beforehand" as a payment line (once)
  const [autoAppliedPrepaid, setAutoAppliedPrepaid] = useState(false);

  useEffect(() => {
    if (autoAppliedPrepaid) return;
    if (!freshReservation) return;
    if (readOnly) return; // do not mutate UI in readOnly
    if (initializedFromExisting) return; // existing handover already has its own payments

    const paid = safeNum(freshReservation.amountPaid);
    const cur =
      (freshReservation.rentalCurrency ||
        freshReservation.depositCurrency ||
        defaultCurrency ||
        '').trim().toUpperCase();

    if (!paid || paid <= 0) return;

    setPayments((prev) => {
      const list = Array.isArray(prev) ? prev : [];

      // If there is already a meaningful payment line, don't duplicate
      const alreadyHasSame = list.some(
        (p) =>
          String(p?.method || '').toUpperCase() === 'PREPAID' &&
          safeNum(p?.amount) === paid &&
          String(p?.currency || '').toUpperCase() === cur
      );
      if (alreadyHasSame) return list;

      // If user already entered anything, don't override their work
      // If user already entered anything meaningful, don't override their work.
      // IMPORTANT: the default initial row has method=CASH so don't treat that alone as "typed".
      const userHasTyped = list.some((p) => {
        const method = String(p?.method || '').trim().toUpperCase();
        const amount = String(p?.amount ?? '').trim();
        const note = String(p?.note ?? '').trim();
        const currency = String(p?.currency || '').trim().toUpperCase();

        const isDefaultEmptyRow =
          (method === 'CASH' || method === '') &&
          amount === '' &&
          note === '' &&
          (currency === '' || currency === String(defaultCurrency || '').toUpperCase());

        // Anything not matching the default empty row counts as user input
        return !isDefaultEmptyRow;
      });
      if (userHasTyped) return list;


      // Replace the default empty CASH row with PREPAID
      return [
        {
          method: 'PREPAID',
          amount: fmtMoney(paid), // string for your inputs
          currency: cur || defaultCurrency,
          note: '',
        },
      ];
    });

    setAutoAppliedPrepaid(true);
  }, [
    autoAppliedPrepaid,
    freshReservation?.id,
    freshReservation?.amountPaid,
    freshReservation?.rentalCurrency,
    freshReservation?.depositCurrency,
    defaultCurrency,
    readOnly,
    initializedFromExisting,
  ]);

  async function handleSubmit(e) {
    e.preventDefault();
    if (submitting) return;

    if (readOnly) {
      showAlert('error', 'Handover locked', 'This pick-up handover is read-only and cannot be edited.');
      return;
    }

    // ===== Required fields validation =====
    const missing = [];
    const isEmpty = (v) => v === null || v === undefined || String(v).trim() === '';

    // Agreement
    if (isEmpty(form.agreementDate)) missing.push('Agreement date');
    if (isEmpty(form.agreementLocation)) missing.push('Agreement place');

    // Lessee details
    if (isEmpty(form.lesseeFirstName)) missing.push('Lessee first name');
    if (isEmpty(form.lesseeLastName)) missing.push('Lessee last name');
    if (isEmpty(form.lesseeEmail)) missing.push('Lessee email');
    if (isEmpty(form.lesseeEmailConfirm)) missing.push('Confirm email');
    if (isEmpty(form.lesseePhone)) missing.push('Mobile number');
    if (isEmpty(form.idNumber)) missing.push('ID / passport number');
    if (isEmpty(form.drivingLicenseNumber)) missing.push('Driving licence number');
    if (isEmpty(form.drivingLicenseCountry)) missing.push('Driving licence issued in');

    // Vehicle condition
    if (isEmpty(form.carId)) missing.push('Car');
    if (isEmpty(form.currentFuel)) missing.push('Fuel level at pick-up');
    if (isEmpty(form.odometer)) missing.push('Odometer (km)');

    // Rental period
    if (isEmpty(form.pickupDate)) missing.push('Pick-up date');
    if (isEmpty(form.pickupTime)) missing.push('Pick-up time');
    if (isEmpty(form.pickupLocation)) missing.push('Pick-up location');
    if (isEmpty(form.dropoffDate)) missing.push('Drop-off date');
    if (isEmpty(form.dropoffTime)) missing.push('Drop-off time');
    if (isEmpty(form.dropoffLocation)) missing.push('Drop-off location');
    if (!rentalDays) missing.push('Valid rental period (check pickup/drop-off dates & times)');

    // Pricing (amount + currency per field)
    if (isEmpty(form.dailyRateAmount)) missing.push('Daily rate amount');
    if (isEmpty(form.dailyRateCurrency)) missing.push('Daily rate currency');

    if (isEmpty(form.extrasCurrency)) missing.push('Extras currency');

    // Deposit
    if (isEmpty(form.depositAmount)) missing.push('Deposit amount');
    if (isEmpty(form.depositCurrency)) missing.push('Deposit currency');

    // Payments: allow empty if you want, but if any line has data, require complete fields
    const rawPayments = (payments || [])
      .map((p) => ({
        method: String(p?.method || '').trim().toUpperCase(),
        amount: String(p?.amount ?? '').trim(),
        currency: String(p?.currency || '').trim().toUpperCase(),
        note: String(p?.note || '').trim(),
      }))
      .filter((p) => p.method || p.amount || p.currency || p.note);

    for (const p of rawPayments) {
      if (isEmpty(p.method)) missing.push('Payment method');
      if (isEmpty(p.amount)) missing.push('Payment amount');
      if (isEmpty(p.currency)) missing.push('Payment currency');
    }

    // Files

    if (missing.length > 0) {
      showAlert('error', 'Missing required fields', `Please fill all required fields: ${missing.join(', ')}.`);
      return;
    }

    if (emailMismatch) {
      showAlert('error', 'Email mismatch', 'Lessee email and confirmation do not match.');
      return;
    }

    if (!reservation?.CompanyId) {
      showAlert('error', 'Missing agency', 'Reservation is missing CompanyId – cannot submit handover.');
      return;
    }

    if (!form.lesseeSignatureDataUrl) {
      showAlert('error', 'Signature required', 'Lessee pick-up signature is required before saving.');
      return;
    }

    if (pendingPhotoUploads > 0) {
      showAlert(
        'error',
        'Uploads still running',
        `Wait for ${pendingPhotoUploads} photo upload${pendingPhotoUploads === 1 ? '' : 's'} to finish before saving.`
      );
      return;
    }

    if (failedPhotoUploads > 0) {
      showAlert(
        'error',
        'Photo uploads failed',
        `Remove or re-upload the ${failedPhotoUploads} failed photo${failedPhotoUploads === 1 ? '' : 's'} before saving.`
      );
      return;
    }

    try {
      setSubmitting(true);
      setSubmitStartedAt(Date.now());
      setBanner(null);

      setStage('prep', 2, 'Preparing handover…');

      try {
        window.localStorage.setItem(`handover_${reservationId}_pickupFuel`, form.currentFuel || '');
      } catch { }

      const fuelLevelPercent = fuelStringToPercent(form.currentFuel);

      const selectedCarForSubmit =
        selectedCar ||
        (cars || []).find((c) => c.id === (form.carId || reservation?.carId)) ||
        null;

      const carName = selectedCarForSubmit?.name || null;
      const carPlate = selectedCarForSubmit?.plate || null;

      const idKeys = uploadedItemsToKeys(idUploads);

      const odoFuelKeys = uploadedItemsToKeys(odoFuelUploads);

      const carPhotoKeys = uploadedItemsToKeys(carPhotoUploads);

      // Signature upload
      setStage('upload_sig', 45, 'Uploading signature…');
      let signatureKey = null;
      if (form.lesseeSignatureDataUrl) {
        const sigFile = dataUrlToFile(form.lesseeSignatureDataUrl, `lessee_signature_${reservationId}.png`);
        if (sigFile) {
          const sigKeys = await uploadFiles('handover/pickup/signature', reservationId, [sigFile]);
          signatureKey = sigKeys[0] || null;
        }
      }

      setStage('save', 78, 'Saving handover record…');

      const cleanPayments = (payments || [])
        .map((p) => ({
          method: String(p?.method || '').trim().toUpperCase(),
          amount: safeNum(p?.amount),
          currency: String(p?.currency || '').trim().toUpperCase(),
          note: String(p?.note || '').trim(),
        }))
        .filter((p) => p.method && p.currency && p.amount);

      const equipmentNotesObj = {
        ...form,
        includeOriginallyReservedCar:
          !!form.includeOriginallyReservedCar && originalReservedCarDiffersFromPickupCar,
        originalReservedCarId:
          form.includeOriginallyReservedCar && originalReservedCarDiffersFromPickupCar
            ? originalReservedCar?.id || null
            : null,
        originalReservedCarName:
          form.includeOriginallyReservedCar && originalReservedCarDiffersFromPickupCar
            ? originalReservedCar?.name || null
            : null,
        originalReservedCarPlate:
          form.includeOriginallyReservedCar && originalReservedCarDiffersFromPickupCar
            ? originalReservedCar?.plate || null
            : null,
        rentalDays,
        totalExtras,
        totalRental,
        totalInsurance,
        deliveryCharge,
        totalsByCurrency,
        reservedCrossBorderCountries,
        payments: cleanPayments,
      };
      delete equipmentNotesObj.lesseeSignatureDataUrl;

      const input = {
        CompanyId: reservation.CompanyId,
        reservationId,
        type: 'PICKUP',
        filledBySub: claims?.sub || null,
        filledByEmail: claims?.email || null,
        customerName: `${form.lesseeFirstName} ${form.lesseeLastName}`
          .trim()
          .replace(/\s+/g, ' ') || null,
        customerEmail: form.lesseeEmail || null,
        customerPhone: form.lesseePhone || null,
        licenseNumber: form.drivingLicenseNumber || form.idNumber || null,
        carId: form.carId || reservation?.carId || null,
        carName,
        carPlate,
        odometerKm: form.odometer ? parseInt(form.odometer, 10) : null,
        fuelLevelPercent,
        exteriorCondition: null,
        interiorCondition: null,
        damageNotes: null,
        equipmentNotes: JSON.stringify(equipmentNotesObj),
        carPhotoKeys: [...idKeys, ...odoFuelKeys, ...carPhotoKeys],
        damagePhotoKeys: [],
        signatureKey,
        ipAddress: null,
        signedAt: new Date().toISOString(),
      };

      const result = await client.graphql({
        query: SubmitHandoverForm,
        variables: { input },
        authMode: 'userPool',
      });

      const ok = result?.data?.submitHandoverForm;
      if (!ok) throw new Error('Mutation returned false');

      // Update reservation status (best effort)
      setStage('update_res', 92, 'Updating reservation checklist…');
      if (reservation?.id) {
        try {
          const updateInput = {
            id: reservation.id,
            pickupChecklistStatus: 'COMPLETED',
            ...(reservation._version != null ? { _version: reservation._version } : {}),
          };
          await client.graphql({
            query: UpdateReservationChecklist,
            variables: { input: updateInput },
            authMode: 'userPool',
          });
        } catch (e) {
          console.warn('Failed to update reservation pickupChecklistStatus', e);
        }
      }

      setStage('done', 100, 'Done');
      setSubmitted(true);
      showAlert('success', 'Pickup handover saved', 'The pick-up handover was saved and emailed to the customer.');
    } catch (err) {
      console.error('Pickup handover submit failed', err);
      showAlert('error', 'Save failed', 'Failed to submit pick-up handover. Check console for details.');
    } finally {
      setSubmitting(false);
    }
  }
  const paymentLabel = useMemo(() => buildPaymentLabel(freshReservation), [
    freshReservation?.paymentStatus,
    freshReservation?.rentalAmount,
    freshReservation?.amountPaid,
    freshReservation?.rentalCurrency,
    freshReservation?.depositCurrency,
  ]);

  // Banner styles
  const bannerColor =
    banner?.type === 'error'
      ? { bg: COLORS.dangerLight, border: COLORS.danger, text: COLORS.danger }
      : banner?.type === 'success'
        ? { bg: COLORS.successLight, border: COLORS.success, text: COLORS.success }
        : { bg: COLORS.primaryLight, border: COLORS.primary, text: COLORS.primaryDark };

  // ✅ Helper for read-only image grids
  const renderImageGrid = (items, emptyText) => {
    if (!items || items.length === 0) {
      return (
        <div style={{ fontSize: 12, color: COLORS.gray[500], marginTop: 4, lineHeight: 1.5 }}>
          {emptyText}
        </div>
      );
    }
    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 6 }}>
        {items.map((p) => (
          <a
            key={p.key}
            href={p.url}
            target="_blank"
            rel="noreferrer"
            style={{
              display: 'inline-block',
              borderRadius: 10,
              overflow: 'hidden',
              border: `1px solid ${COLORS.gray[200]}`,
              transition: 'transform 0.2s ease',
            }}
            onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
            onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
          >
            <img
              src={p.url}
              alt=""
              style={{ width: 100, height: 75, objectFit: 'cover', display: 'block' }}
            />
          </a>
        ))}
      </div>
    );
  };

  return (
    <form
      onSubmit={handleSubmit}
      style={{ display: 'grid', gap: 20, width: '100%', maxWidth: '100%' }}
    >
      <fieldset disabled={readOnly || submitting || submitted} style={readOnly ? { opacity: 0.85 } : undefined}>
        {/* In-form banner (fallback if no global toast) */}
        {banner && (
          <div
            style={{
              marginBottom: 14,
              padding: '12px 14px',
              borderRadius: 10,
              border: 'none',
              background: bannerColor.bg,
              color: bannerColor.text,
              fontSize: 13,
              fontWeight: 500,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              gap: 10,
            }}
          >
            <div>
              {banner.title && <div style={{ fontWeight: 700 }}>{banner.title}</div>}
              {banner.message && <div style={{ marginTop: 4, lineHeight: 1.4 }}>{banner.message}</div>}
            </div>
            <button
              type="button"
              onClick={() => setBanner(null)}
              style={{
                border: 'none',
                background: 'transparent',
                cursor: 'pointer',
                fontSize: 16,
                lineHeight: 1,
                padding: '2px',
                color: bannerColor.text,
                opacity: 0.6,
                transition: 'opacity 0.2s ease',
              }}
              onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
              onMouseLeave={(e) => e.currentTarget.style.opacity = '0.6'}
            >
              ✕
            </button>
          </div>
        )}
        {paymentLabel && (
          <div
            style={{
              border: 'none',
              background:
                paymentLabel.tone === 'good'
                  ? COLORS.successLight
                  : paymentLabel.tone === 'warn'
                    ? COLORS.warningLight
                    : COLORS.dangerLight,
              color:
                paymentLabel.tone === 'good'
                  ? COLORS.success
                  : paymentLabel.tone === 'warn'
                    ? '#92400e'
                    : COLORS.danger,
              borderRadius: 12,
              padding: '12px 14px',
              fontSize: 13,
              fontWeight: 700,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 12,
              boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
            }}
          >
            <span>Payment Status</span>
            <span style={{ fontWeight: 800, fontSize: 14 }}>{paymentLabel.text}</span>
          </div>
        )}

        {/* Agreement section */}
        <section style={cardStyle()}>
          <div style={twoColResponsive}>
            <label>
              <span style={labelStyle}>Agreement date</span>
              <input
                type="date"
                style={inputStyle}
                value={form.agreementDate}
                onChange={(e) => updateField('agreementDate', e.target.value)}
              />
            </label>
            <label>
              <span style={labelStyle}>Agreement place</span>
              <input
                style={inputStyle}
                value={form.agreementLocation}
                onChange={(e) => updateField('agreementLocation', e.target.value)}
              />
            </label>
          </div>
        </section>

        {/* Lessee details */}
        <section style={cardStyle()}>
          <h3 style={sectionTitleStyle}>Lessee details</h3>
          <div style={twoColResponsive}>
            <label>
              <span style={labelStyle}>First name</span>
              <input
                style={inputStyle}
                value={form.lesseeFirstName}
                onChange={(e) => updateField('lesseeFirstName', e.target.value)}
              />
            </label>
            <label>
              <span style={labelStyle}>Last name</span>
              <input
                style={inputStyle}
                value={form.lesseeLastName}
                onChange={(e) => updateField('lesseeLastName', e.target.value)}
              />
            </label>
          </div>

          <div style={{ ...twoColResponsive, marginTop: 10 }}>
            <label>
              <span style={labelStyle}>Email</span>
              <input
                type="email"
                style={inputStyle}
                value={form.lesseeEmail}
                onChange={(e) => {
                  setLesseeEmailTouched(true);
                  updateField('lesseeEmail', e.target.value);
                }}
                onBlur={() => setLesseeEmailTouched(true)}
              />
            </label>
            <label>
              <span style={labelStyle}>Confirm email</span>
              <input
                type="email"
                style={{
                  ...inputStyle,
                  borderColor:
                    emailMismatch && (lesseeEmailTouched || lesseeEmailConfirmTouched)
                      ? COLORS.danger
                      : COLORS.gray[300],
                  background:
                    emailMismatch && (lesseeEmailTouched || lesseeEmailConfirmTouched)
                      ? COLORS.dangerLight
                      : '#ffffff',
                }}
                value={form.lesseeEmailConfirm}
                onChange={(e) => {
                  setLesseeEmailConfirmTouched(true);
                  updateField('lesseeEmailConfirm', e.target.value);
                }}
                onBlur={() => setLesseeEmailConfirmTouched(true)}
              />
              {emailMismatch && (lesseeEmailTouched || lesseeEmailConfirmTouched) && (
                <div style={{ fontSize: 11, color: COLORS.danger, marginTop: 4, fontWeight: 500 }}>
                  Emails do not match
                </div>
              )}
            </label>
          </div>

          <div style={{ ...twoColResponsive, marginTop: 10 }}>
            <label>
              <span style={labelStyle}>Mobile number</span>
              <input
                type="tel"
                style={inputStyle}
                value={form.lesseePhone}
                onChange={(e) => updateField('lesseePhone', e.target.value)}
              />
            </label>
            <label>
              <span style={labelStyle}>ID / passport number</span>
              <input
                style={inputStyle}
                value={form.idNumber}
                onChange={(e) => updateField('idNumber', e.target.value)}
              />
            </label>
          </div>

          <div style={{ ...twoColResponsive, marginTop: 10 }}>
            <label>
              <span style={labelStyle}>Driving licence number</span>
              <input
                style={inputStyle}
                value={form.drivingLicenseNumber}
                onChange={(e) => updateField('drivingLicenseNumber', e.target.value)}
              />
            </label>
            <label>
              <span style={labelStyle}>Driving licence issued in</span>
              <input
                style={inputStyle}
                value={form.drivingLicenseCountry}
                onChange={(e) => updateField('drivingLicenseCountry', e.target.value)}
              />
            </label>
          </div>

          {readOnly ? (
            <div style={{ marginTop: 10 }}>
              <span style={labelStyle}>ID / passport copy</span>
              {renderImageGrid(existingIdPhotoUrls, 'No ID / passport copy uploaded for this handover.')}
            </div>
          ) : (
            <FileUploadWithPreview
              label="Upload ID / passport copy"
              uploads={idUploads}
              onUploadsChange={setIdUploads}
              uploadPrefix="handover/pickup/id"
              reservationId={reservationId}
              disabled={submitted}
              preserveUploadedOnUnmount={submitted}
            />
          )}
        </section>

        {/* Vehicle at pickup */}
        <section style={cardStyle()}>
          <h3 style={sectionTitleStyle}>Vehicle condition at pick-up</h3>

          <div style={twoColResponsive}>
            <label>
              <span style={labelStyle}>Car</span>
              <select
                style={inputStyle}
                value={form.carId}
                onChange={(e) => updateField('carId', e.target.value)}
              >
                <option value="">Select car…</option>
                {carOptions.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </select>
            </label>

            <label>
              <span style={labelStyle}>Car delivered</span>
              <select
                style={inputStyle}
                value={form.carDeliveredWashed}
                onChange={(e) => updateField('carDeliveredWashed', e.target.value)}
              >
                <option value="washed">Washed</option>
                <option value="not_washed">Not washed</option>
              </select>
            </label>
          </div>

          {originalReservedCar && originalReservedCarDiffersFromPickupCar && (
            <label
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 10,
                marginTop: 12,
                cursor: readOnly || submitting || submitted ? 'default' : 'pointer',
              }}
            >
              <input
                type='checkbox'
                checked={!!form.includeOriginallyReservedCar}
                onChange={(e) =>
                  updateField('includeOriginallyReservedCar', e.target.checked)
                }
                style={{ marginTop: 3 }}
              />
              <span>
                <span style={labelStyle}>Include originally reserved vehicle in contract</span>
                <span style={{ ...helperTextStyle, display: 'block', marginTop: 3 }}>
                  {originalReservedCar.label}
                </span>
              </span>
            </label>
          )}

          <div style={{ ...twoColResponsive, marginTop: 10 }}>
            <label>
              <span style={labelStyle}>Fuel level at pick-up</span>
              <select
                style={inputStyle}
                value={form.currentFuel}
                onChange={(e) => updateField('currentFuel', e.target.value)}
              >
                <option value="">Select…</option>
                <option value="1/4">¼ tank</option>
                <option value="1/2">½ tank</option>
                <option value="3/4">¾ tank</option>
                <option value="full">Full tank</option>
              </select>
            </label>

            <label>
              <span style={labelStyle}>Odometer (km)</span>
              <input
                type="number"
                style={inputStyle}
                value={form.odometer}
                onChange={(e) => updateField('odometer', e.target.value)}
              />
            </label>
          </div>

          {readOnly ? (
            <div style={{ marginTop: 10 }}>
              <span style={labelStyle}>Odometer / fuel photos</span>
              {renderImageGrid(existingOdoFuelUrls, 'No odometer / fuel photos uploaded for this handover.')}
            </div>
          ) : (
            <FileUploadWithPreview
              label="Upload odometer / fuel photos"
              uploads={odoFuelUploads}
              onUploadsChange={setOdoFuelUploads}
              uploadPrefix="handover/pickup/odo"
              reservationId={reservationId}
              disabled={submitted}
              preserveUploadedOnUnmount={submitted}
            />
          )}

          {readOnly ? (
            <div style={{ marginTop: 10 }}>
              <span style={labelStyle}>Vehicle photos (exterior / interior)</span>
              {renderImageGrid(existingCarPhotoUrls, 'No vehicle photos uploaded for this handover.')}
            </div>
          ) : (
            <FileUploadWithPreview
              label="Upload vehicle photos (exterior / interior)"
              uploads={carPhotoUploads}
              onUploadsChange={setCarPhotoUploads}
              uploadPrefix="handover/pickup/car"
              reservationId={reservationId}
              disabled={submitted}
              preserveUploadedOnUnmount={submitted}
            />
          )}
        </section>

        {/* Rental period */}
        <section style={cardStyle()}>
          <h3 style={sectionTitleStyle}>Rental period</h3>

          <div style={twoColResponsive}>
            <div>
              <h4 style={{ margin: '0 0 6px', fontSize: 13 }}>Pick-up</h4>
              <div style={twoColResponsive}>
                <label>
                  <span style={labelStyle}>Date</span>
                  <input
                    type="date"
                    style={inputStyle}
                    value={form.pickupDate}
                    onChange={(e) => updateField('pickupDate', e.target.value)}
                  />
                </label>
                <label>
                  <span style={labelStyle}>Time</span>
                  <input
                    type="time"
                    style={inputStyle}
                    value={form.pickupTime}
                    onChange={(e) => updateField('pickupTime', e.target.value)}
                  />
                </label>
              </div>
              <label style={{ marginTop: 8, display: 'block' }}>
                <span style={labelStyle}>Location</span>
                <input
                  style={inputStyle}
                  value={form.pickupLocation}
                  onChange={(e) => updateField('pickupLocation', e.target.value)}
                  placeholder="Airport / office / hotel…"
                />
              </label>
            </div>

            <div>
              <h4 style={{ margin: '0 0 6px', fontSize: 13 }}>Drop-off (planned)</h4>
              <div style={twoColResponsive}>
                <label>
                  <span style={labelStyle}>Date</span>
                  <input
                    type="date"
                    style={inputStyle}
                    value={form.dropoffDate}
                    onChange={(e) => updateField('dropoffDate', e.target.value)}
                  />
                </label>
                <label>
                  <span style={labelStyle}>Time</span>
                  <input
                    type="time"
                    style={inputStyle}
                    value={form.dropoffTime}
                    onChange={(e) => updateField('dropoffTime', e.target.value)}
                  />
                </label>
              </div>
              <label style={{ marginTop: 8, display: 'block' }}>
                <span style={labelStyle}>Location</span>
                <input
                  style={inputStyle}
                  value={form.dropoffLocation}
                  onChange={(e) => updateField('dropoffLocation', e.target.value)}
                  placeholder="Airport / office / hotel…"
                />
              </label>
            </div>
          </div>

          <div style={{ marginTop: 10 }}>
            <span style={labelStyle}>Rental days (auto)</span>
            <input
              style={{ ...inputStyle, background: COLORS.gray[100] }}
              readOnly
              value={rentalDays || ''}
              placeholder="0"
            />
          </div>
        </section>

        {/* Pricing / extras */}
        <section style={cardStyle()}>
          <h3 style={sectionTitleStyle}>Pricing & extras</h3>

          <div style={threeColResponsive}>
            <MoneyInput
              label="Daily rate"
              amount={form.dailyRateAmount}
              currency={form.dailyRateCurrency}
              onAmount={(v) => updateField('dailyRateAmount', v)}
              onCurrency={(v) => updateField('dailyRateCurrency', v)}
              isMobile={isMobile}
            />
            <MoneyInput
              label="Insurance per day"
              amount={form.insurancePerDayAmount}
              currency={form.insurancePerDayCurrency}
              onAmount={(v) => updateField('insurancePerDayAmount', v)}
              onCurrency={(v) => updateField('insurancePerDayCurrency', v)}
              isMobile={isMobile}
            />
            <MoneyInput
              label="Delivery / Pickup charge"
              amount={form.deliveryChargeAmount}
              currency={form.deliveryChargeCurrency}
              onAmount={(v) => updateField('deliveryChargeAmount', v)}
              onCurrency={(v) => updateField('deliveryChargeCurrency', v)}
              isMobile={isMobile}
            />
          </div>

          <div style={{ marginTop: 12 }}>
            <span style={labelStyle}>Extras currency</span>
            <select
              style={{ ...inputStyle, maxWidth: isMobile ? '100%' : 200 }}
              value={form.extrasCurrency}
              onChange={(e) => updateField('extrasCurrency', e.target.value)}
            >
              <option value="">Select…</option>
              {CURRENCY_OPTIONS.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>

          <div style={{ marginTop: 12 }}>
            <span style={labelStyle}>
              Extras (
              {isAlbania
                ? '12 EUR/day (drivers & seats), snow chains included'
                : '5 per day (all extras)'}
              )
            </span>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, fontSize: 13 }}>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <input type="checkbox" checked={form.extrasCarSeat} onChange={() => handleCheckbox('extrasCarSeat')} />
                Child seat
                <span style={{ fontSize: 11, color: COLORS.gray[500] }}>
                  {isAlbania ? ' (+12/day)' : ' (+5/day)'}
                </span>
              </label>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <input type="checkbox" checked={form.extrasBooster} onChange={() => handleCheckbox('extrasBooster')} />
                Booster seat
                <span style={{ fontSize: 11, color: COLORS.gray[500] }}>
                  {isAlbania ? ' (+12/day)' : ' (+5/day)'}
                </span>
              </label>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <input
                  type="checkbox"
                  checked={form.extrasSnowChains}
                  onChange={() => handleCheckbox('extrasSnowChains')}
                />
                Snow chains
                <span style={{ fontSize: 11, color: COLORS.gray[500] }}>
                  {isAlbania ? ' (included in base rate)' : ' (+5/day)'}
                </span>
              </label>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <input
                  type="checkbox"
                  checked={form.extrasExtraDriver1}
                  onChange={() => handleCheckbox('extrasExtraDriver1')}
                />
                Extra driver 1
                <span style={{ fontSize: 11, color: COLORS.gray[500] }}>
                  {isAlbania ? ' (+12/day)' : ' (+5/day)'}
                </span>
              </label>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <input
                  type="checkbox"
                  checked={form.extrasExtraDriver2}
                  onChange={() => handleCheckbox('extrasExtraDriver2')}
                />
                Extra driver 2
                <span style={{ fontSize: 11, color: COLORS.gray[500] }}>
                  {isAlbania ? ' (+12/day)' : ' (+5/day)'}
                </span>
              </label>
            </div>
            {reservedCrossBorderCountries.length ? (
              <div style={{ marginTop: 10, ...helperTextStyle, color: COLORS.gray[700] }}>
                Reserved cross-border countries: <strong>{reservedCrossBorderCountries.join(', ')}</strong>
              </div>
            ) : null}
          </div>

          <div style={{ ...threeColResponsive, marginTop: 12 }}>
            <label>
              <span style={labelStyle}>Total rental</span>
              <input
                style={{ ...inputStyle, background: COLORS.gray[100] }}
                readOnly
                value={totalRental ? fmtMoney(totalRental) : ''}
                placeholder="0.00"
              />
              <div style={{ fontSize: 11, color: COLORS.gray[500], marginTop: 2 }}>
                Currency: <strong>{form.dailyRateCurrency || '—'}</strong>
              </div>
            </label>

            <label>
              <span style={labelStyle}>Total extras</span>
              <input
                style={{ ...inputStyle, background: COLORS.gray[100] }}
                readOnly
                value={totalExtras ? fmtMoney(totalExtras) : ''}
                placeholder="0.00"
              />
              <div style={{ fontSize: 11, color: COLORS.gray[500], marginTop: 2 }}>
                Currency: <strong>{form.extrasCurrency || '—'}</strong>
              </div>
            </label>

            <label>
              <span style={labelStyle}>Total insurance</span>
              <input
                style={{ ...inputStyle, background: COLORS.gray[100] }}
                readOnly
                value={totalInsurance ? fmtMoney(totalInsurance) : ''}
                placeholder="0.00"
              />
              <div style={{ fontSize: 11, color: COLORS.gray[500], marginTop: 2 }}>
                Currency: <strong>{form.insurancePerDayCurrency || '—'}</strong>
              </div>
            </label>
          </div>

          <div style={{ marginTop: 12 }}>
            <span style={labelStyle}>Totals by currency (no conversion)</span>
            <div style={{ display: 'grid', gap: 8, marginTop: 6 }}>
              {Object.keys(totalsByCurrency).length === 0 ? (
                <div style={{ fontSize: 12, color: COLORS.gray[500] }}>No totals yet.</div>
              ) : (
                Object.entries(totalsByCurrency).map(([cur, t]) => (
                  <div
                    key={cur}
                    style={{
                      border: `1px solid ${COLORS.gray[200]}`,
                      background: COLORS.gray[50],
                      borderRadius: 10,
                      padding: '10px 12px',
                      display: 'grid',
                      gap: 4,
                      fontSize: 12,
                      color: COLORS.gray[700],
                    }}
                  >
                    <div style={{ fontWeight: 800 }}>{cur}</div>
                    <div>Rental: {fmtMoney(t.rental)} {cur}</div>
                    <div>Extras: {fmtMoney(t.extras)} {cur}</div>
                    <div>Insurance: {fmtMoney(t.insurance)} {cur}</div>
                    <div>Delivery: {fmtMoney(t.delivery)} {cur}</div>
                    <div style={{ fontWeight: 800, marginTop: 4 }}>
                      Grand: {fmtMoney(t.grand)} {cur}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          <div style={{ ...twoColResponsive, marginTop: 12 }}>
            <label>
              <span style={labelStyle}>Deposit amount</span>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <input
                  type="number"
                  step="0.01"
                  style={{
                    ...inputStyle,
                    maxWidth: isMobile ? '100%' : 160,
                    flex: isMobile ? '1 1 100%' : '0 0 auto',
                  }}
                  placeholder="Amount"
                  value={form.depositAmount}
                  onChange={(e) => updateField('depositAmount', e.target.value)}
                />
                <select
                  style={{
                    ...inputStyle,
                    maxWidth: isMobile ? '100%' : 140,
                    flex: isMobile ? '1 1 100%' : '0 0 auto',
                  }}
                  value={form.depositCurrency}
                  onChange={(e) => updateField('depositCurrency', e.target.value)}
                >
                  <option value="">Currency</option>
                  {CURRENCY_OPTIONS.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>
              {suggestedDeposit && (
                <div style={{ fontSize: 11, color: COLORS.gray[500], marginTop: 4 }}>
                  Suggested deposit:{' '}
                  <strong>
                    {suggestedDeposit} {isAlbania ? 'EUR' : 'USD'}
                  </strong>
                  {selectedCar?.category ? ` (${selectedCar.category})` : ''}
                </div>
              )}
            </label>
            <div />
          </div>

          <label style={{ marginTop: 12, display: 'block' }}>
            <span style={labelStyle}>Additional notes</span>
            <textarea
              style={textareaStyle}
              value={form.extraNotes}
              onChange={(e) => updateField('extraNotes', e.target.value)}
            />
          </label>
        </section>

        {/* Payments */}
        <section style={cardStyle()}>
          <h3 style={sectionTitleStyle}>Payments received</h3>

          <div style={{ fontSize: 12, color: COLORS.gray[500], marginTop: 4 }}>
            Add one or more payment lines (no conversion). Example: part card, part cash.
          </div>

          <div style={{ display: 'grid', gap: 10, marginTop: 10 }}>
            {payments.map((p, idx) => (
              <div
                key={idx}
                style={{
                  border: `1px solid ${COLORS.gray[200]}`,
                  borderRadius: 10,
                  padding: 10,
                  background: COLORS.gray[50],
                  display: 'grid',
                  gap: 8,
                }}
              >
                <div style={threeColResponsive}>
                  <label>
                    <span style={labelStyle}>Method</span>
                    <select
                      style={inputStyle}
                      value={p.method}
                      onChange={(e) => updatePayment(idx, { method: e.target.value })}
                    >
                      <option value="PREPAID">Prepaid</option>
                      <option value="CARD">Card</option>
                      <option value="CASH">Cash</option>
                      <option value="BANK_TRANSFER">Bank transfer</option>
                      <option value="OTHER">Other</option>
                    </select>
                  </label>

                  <MoneyInput
                    label="Amount"
                    amount={p.amount}
                    currency={p.currency}
                    onAmount={(v) => updatePayment(idx, { amount: v })}
                    onCurrency={(v) => updatePayment(idx, { currency: v })}
                    isMobile={isMobile}
                  />

                  <label>
                    <span style={labelStyle}>Note (optional)</span>
                    <input
                      style={inputStyle}
                      value={p.note}
                      onChange={(e) => updatePayment(idx, { note: e.target.value })}
                      placeholder="Receipt / ref / comment…"
                    />
                  </label>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                  <div style={{ fontSize: 11, color: COLORS.gray[500] }}>Line #{idx + 1}</div>
                  {payments.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removePaymentRow(idx)}
                      style={{
                        border: `1px solid ${COLORS.gray[300]}`,
                        background: '#fff',
                        borderRadius: 8,
                        padding: '6px 10px',
                        cursor: 'pointer',
                        fontSize: 12,
                      }}
                    >
                      Remove
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          <button
            type="button"
            onClick={addPaymentRow}
            style={{
              marginTop: 10,
              border: `1px solid ${COLORS.gray[300]}`,
              background: '#fff',
              borderRadius: 10,
              padding: '8px 12px',
              cursor: 'pointer',
              fontSize: 12,
              width: 'fit-content',
            }}
          >
            + Add payment line
          </button>
        </section>

        {/* Signatures */}
        <section style={cardStyle()}>
          <h3 style={sectionTitleStyle}>Signatures</h3>

          <div style={{ marginBottom: 10 }}>
            <span style={labelStyle}>Lessor (Haste LLC)</span>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
              <input type="radio" checked readOnly />
              Haste LLC E-Signature Digital ID 55712233254412
            </label>
          </div>

          <div style={{ marginTop: 12 }}>
            <span style={labelStyle}>Lessee signature</span>
            <p style={{ fontSize: 12, color: COLORS.gray[400], marginBottom: 4 }}>
              {readOnly
                ? 'Signature already captured and stored with this handover record.'
                : 'Customer signs electronically on screen. The captured signature is stored with this handover record.'}
            </p>
            {readOnly ? (
              <div
                style={{
                  borderRadius: 8,
                  border: `1px dashed ${COLORS.gray[300]}`,
                  padding: 12,
                  fontSize: 12,
                  color: COLORS.gray[500],
                  background: COLORS.gray[50],
                }}
              >
                Existing signature on file.
              </div>
            ) : (
              <SignaturePad
                height={180}
                onChange={(dataUrl) => updateField('lesseeSignatureDataUrl', dataUrl)}
              />
            )}
          </div>

          {claims?.email && (
            <div style={{ marginTop: 12, fontSize: 12, color: COLORS.gray[500] }}>
              Staff account: {claims.email}
            </div>
          )}
        </section>

        {/* Contract section (collapsible) */}
        <section style={cardStyle()}>
          <h3 style={sectionTitleStyle}>Contract terms & conditions</h3>

          <button
            type="button"
            onClick={() => setShowContract((v) => !v)}
            style={{
              marginTop: 4,
              marginBottom: 6,
              fontSize: 12,
              borderRadius: 999,
              border: `1px solid ${COLORS.gray[300]}`,
              padding: '4px 10px',
              background: COLORS.gray[50],
              cursor: 'pointer',
            }}
          >
            {showContract ? 'Hide contract text ▲' : 'Show contract text ▼'}
          </button>

          {!showContract && (
            <p style={{ fontSize: 11, color: COLORS.gray[400], marginTop: 4 }}>
              Contract text is hidden to save space. Click to view the full{' '}
              {location === 'AL' ? 'Albanian' : 'Georgian'} rental terms.
            </p>
          )}

          {showContract && (
            <p
              style={{
                fontSize: 12,
                color: COLORS.gray[600],
                whiteSpace: 'pre-wrap',
                borderRadius: 8,
                border: `1px solid ${COLORS.gray[200]}`,
                padding: 10,
                background: COLORS.gray[50],
                maxHeight: 260,
                overflowY: 'auto',
              }}
            >
              {contractText}
            </p>
          )}
        </section>
      </fieldset>

      {/* Submit bar */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, alignItems: 'center' }}>
        {submitting && (
          <div style={{ flex: 1, maxWidth: 420 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: COLORS.gray[500] }}>
              <span>{submitDetail || 'Working…'}</span>
              <span>{Math.round(submitPct)}%</span>
            </div>
            <div
              style={{
                height: 8,
                background: COLORS.gray[200],
                borderRadius: 999,
                overflow: 'hidden',
                marginTop: 6,
              }}
            >
              <div style={{ height: '100%', width: `${submitPct}%`, background: '#2563eb' }} />
            </div>
          </div>
        )}

        {submitted && !submitting && (
          <div
            style={{
              flex: 1,
              padding: '10px 12px',
              borderRadius: 10,
              border: `1px solid ${COLORS.successLight}`,
              background: COLORS.successLight,
              color: COLORS.success,
              fontSize: 12,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 10,
            }}
          >
            <div>
              <div style={{ fontWeight: 700 }}>Pickup handover saved.</div>
              <div style={{ marginTop: 2, opacity: 0.9 }}>Email will be sent shortly (if configured).</div>
            </div>
            <span style={{ fontWeight: 800, fontSize: 14 }}>✓</span>
          </div>
        )}

        {!readOnly && !submitted && (
          <Button
            type="submit"
            disabled={submitting || pendingPhotoUploads > 0 || failedPhotoUploads > 0}
          >
            {submitting
              ? 'Submitting…'
              : pendingPhotoUploads > 0
              ? `Uploading photos (${pendingPhotoUploads})…`
              : failedPhotoUploads > 0
              ? 'Resolve failed uploads'
              : 'Save pick-up handover'}
          </Button>
        )}
      </div>
    </form>
  );
}
